/* Gestion des quotas par plan — Factumis app.

   Modèle « soft-limit » (cf. app/server middleware/quota.ts) : l'émission de factures
   n'est JAMAIS bloquée au quota du plan — promesse « aucune facture bloquée pendant le
   mois en cours ». On signale seulement le dépassement pour inviter à monter en gamme.
   Le seul blocage dur est un plafond anti-abus côté serveur (quota × multiplicateur),
   renvoyé en HTTP 402 et géré au moment de l'envoi (CreateInvoice).

   - Factures émises : non bloquant ici ; le 402 (plafond) est traité à l'envoi.
   - Factures reçues : quota purement informatif, jamais bloquant.

   `QuotaLevel` : ok (< 80 %) → warn (≥ 80 % et < 100 %) → over (≥ 100 %, dépassé mais
   toujours autorisé). Si quota = 0 (plan non chargé/illimité), level = 'ok' par convention. */
import type { Me } from './useMe';

export type QuotaLevel = 'ok' | 'warn' | 'over';

export interface QuotaState { level: QuotaLevel; used: number; quota: number; pct: number; }

function quotaState(used: number, quota: number): QuotaState {
  const pct = quota > 0 ? Math.min((used / quota) * 100, 100) : 0;
  const level: QuotaLevel = quota > 0 && used >= quota ? 'over' : pct >= 80 ? 'warn' : 'ok';
  return { level, used, quota, pct };
}

/** Quota mensuel de factures ÉMISES. Soft-limit : 'over' n'empêche pas l'émission. */
export function emittedQuotaLevel(me: Me | null): QuotaState {
  return quotaState(me?.usage.emitted_count ?? 0, me?.plan?.emitted_quota ?? 0);
}

/** Quota de factures reçues — purement informatif (jamais bloquant). */
export function receivedQuotaLevel(me: Me | null): QuotaState {
  return quotaState(me?.usage.received_count ?? 0, me?.plan?.received_quota ?? 0);
}

/* Limites secondaires DURES (devis/mois, clients) — contrairement à l'émission,
   l'action est bloquée à la limite (402 serveur). quota null = illimité → pas de
   bandeau. 'reached' = plafond atteint (bloqué) ; 'warn' = ≥ 80 %. */
export type LimitLevel = 'ok' | 'warn' | 'reached';
export interface LimitState { level: LimitLevel; used: number; quota: number | null; remaining: number | null; }

function limitState(used: number, quota: number | null): LimitState {
  if (quota === null || quota <= 0) return { level: 'ok', used, quota, remaining: null }; // illimité
  const remaining = Math.max(quota - used, 0);
  const level: LimitLevel = used >= quota ? 'reached' : used / quota >= 0.8 ? 'warn' : 'ok';
  return { level, used, quota, remaining };
}

/** Plafond mensuel de devis du plan (Conformité = 3/mois ; sup. = illimité). */
export function quoteLimitState(me: Me | null): LimitState {
  return limitState(me?.limits?.quotes.used ?? 0, me?.limits?.quotes.quota ?? null);
}

/** Plafond du carnet clients du plan (Conformité = 10 ; sup. = illimité). */
export function clientLimitState(me: Me | null): LimitState {
  return limitState(me?.limits?.clients.used ?? 0, me?.limits?.clients.quota ?? null);
}

/** Plafond du catalogue produits/services du plan. */
export function catalogLimitState(me: Me | null): LimitState {
  return limitState(me?.limits?.catalog.used ?? 0, me?.limits?.catalog.quota ?? null);
}

/** Plafond de modèles de facture/devis du plan (Conformité = 1, Activité = 3). */
export function templateLimitState(me: Me | null): LimitState {
  return limitState(me?.limits?.templates.used ?? 0, me?.limits?.templates.quota ?? null);
}
